import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../redux-store";
import { selectRouteViewModel } from "../route-views/selectors";
import { DriverSchedule } from "./types";
import { selectAllCargoViews } from "../cargo-views/selectors";
import { DateTime, Interval } from "luxon";
import { addMinutes } from "dora-shared";
import { selectMyTeamsIds } from "../../auth/selectors";
import { selectFeature } from "../../features/selectors";

export const selectStatus = (state: RootState) => state.data.drivers.status;
export const selectDrivers = createSelector(
  (state: RootState) => state.data.drivers.driverEntities,
  (state: RootState) => state.data.drivers.driverIds,
  (entities, ids) => ids.map((id) => entities[id])
);

export const selectShowCannotDeleteDriverHasRoutesDialog = (state: RootState) =>
  state.data.drivers.showCannotDeleteDriverWithRoutesDialog;

export const selectShowCannotArchiveDriverActiveRoutesDialog = (
  state: RootState
) => state.data.drivers.showCannotArchiveDriverActiveRoutesDialog;

export const selectFilterTeamIds = (state: RootState) =>
  state.data.drivers.filterTeamIds;

export const selectDriversFilteredByTeams = createSelector(
  selectDrivers,
  selectFilterTeamIds,
  (drivers, filterTeamIds) =>
    drivers.filter((driver) =>
      driver.teams.some((teamId) => filterTeamIds.includes(teamId))
    )
);

export const selectDriver = (id?: string) => (state: RootState) =>
  (id && state.data.drivers.driverEntities[id]) || null;

// Ids not in the store yet (drivers still loading) are left out rather than
// returned as undefined.
export const selectSpecificDrivers = (ids: string[]) => (state: RootState) =>
  ids
    .map((id) => state.data.drivers.driverEntities[id])
    .filter((driver) => driver !== undefined);

export const selectDriverNos = createSelector(selectDrivers, (drivers) =>
  drivers.map((x) => x.number)
);

export const selectRouteFromToDateTimes = (routeId: string) =>
  createSelector(
    selectRouteViewModel(routeId),
    selectAllCargoViews,
    (routeVM, allCargoViews) => {
      if (!routeVM) {
        return {
          from: null,
          to: null,
        };
      }
      if (!routeVM.cargoOrder.length) {
        // Empty route: no cargo dates to derive from, so fall back to the
        // route's intended start date (treated as a single-day window).
        if (!routeVM.intendedStartDate) {
          return {
            from: null,
            to: null,
          };
        }
        const from = DateTime.fromISO(`${routeVM.intendedStartDate}T00:00`);
        return {
          from,
          to: from.endOf("day"),
        };
      }
      // cargoOrder's position doesn't guarantee chronological order (it's
      // resynced to match cargo dates asynchronously, so it can be
      // transiently - or, for older data, permanently - out of date order).
      // Derive from/to as the true min/max across every cargo instead of
      // trusting position 0 / length-1 to be the chronological extremes,
      // so this can never produce an inverted (from > to) window.
      let from: DateTime | null = null;
      let to: DateTime | null = null;

      for (const cargoId of routeVM.cargoOrder) {
        const cargo = allCargoViews[cargoId];
        if (!cargo) {
          continue;
        }

        const startDate = cargo.firstStop.date;
        const startTimeStart = cargo.firstStop.time
          ? cargo.firstStop.time.split(" - ")[0]
          : "00:00";
        let startTimeEnd = cargo.firstStop.time?.split(" - ")[1] || null;
        if (!startTimeEnd) {
          startTimeEnd = addMinutes(startTimeStart, 60);
        }

        const endStop = cargo.lastStop?.date ? cargo.lastStop : cargo.firstStop;
        const endDate = endStop.date;
        const endTimeParts = endStop.time ? endStop.time.split(" - ") : null;

        let endTime = endTimeParts
          ? endTimeParts[1] ||
            (endTimeParts[0] ? addMinutes(endTimeParts[0], 60) : null)
          : null;

        if (!endTime) {
          endTime =
            endDate === startDate ? addMinutes(startTimeEnd, 60) : "23:59";
        }

        const cargoFrom = DateTime.fromISO(`${startDate}T${startTimeStart}`);
        const cargoTo = DateTime.fromISO(`${endDate}T${endTime}`);

        if (!from || cargoFrom < from) {
          from = cargoFrom;
        }
        if (!to || cargoTo > to) {
          to = cargoTo;
        }
      }

      return { from, to };
    }
  );

export const selectDriversVisibleForUser = createSelector(
  selectDrivers,
  selectMyTeamsIds,
  (drivers, userTeams) =>
    drivers.filter((d) => d.teams.some((t) => userTeams.includes(t)))
);

export const selectAllDriversOrderByHavingConflictingSchedulesWithRoute = (
  routeId: string
) =>
  createSelector(
    (state: RootState) => state,
    selectDriversVisibleForUser,
    (state, drivers) => {
      const driversWithConflictingSchedulesSet = new Set<string>();
      for (const driver of drivers) {
        const { conflictingSchedules } =
          selectDriverSchedulesConflictingWithRoute(driver.id, routeId)(state);
        if (conflictingSchedules.length) {
          driversWithConflictingSchedulesSet.add(driver.id);
        }
      }
      return [
        ...drivers.filter((d) => !driversWithConflictingSchedulesSet.has(d.id)),
        ...drivers.filter((d) => driversWithConflictingSchedulesSet.has(d.id)),
      ];
    }
  );

export const selectDriverSchedulesConflictingWithRoute = (
  driverId: string,
  routeId: string
) =>
  createSelector(
    selectDriver(driverId),
    selectRouteViewModel(routeId),
    selectRouteFromToDateTimes(routeId),
    (driver, route, fromToDateTimes) => {
      const conflictingSchedules: DriverSchedule[] = [];
      const schedulesOnRouteDays: DriverSchedule[] = [];
      if (
        !driver?.schedules.length ||
        !route ||
        !fromToDateTimes.from ||
        !fromToDateTimes.to
      ) {
        return {
          conflictingSchedules: [],
          schedulesOnRouteDays,
        };
      }

      const routeStartJsDate = fromToDateTimes.from.toJSDate();
      const routeEndJsDate = fromToDateTimes.to.toJSDate();

      const recurrentSchedules = driver.schedules.filter(
        (s) => s.type === "RECURRENT"
      );
      const intervalSchedules = driver.schedules.filter(
        (s) => s.type === "INTERVAL"
      );

      const routeInterval = Interval.fromDateTimes(
        fromToDateTimes.from,
        fromToDateTimes.to
      );
      const routeIntervalDays = routeInterval
        .splitBy({ days: 1 })
        .map((i) => i.start.toISODate());

      const getDay = (date: Date) => {
        let d = date.getDay();
        if (d === 0) {
          d = 7;
        }
        return d;
      };

      // RECURRENT
      if (recurrentSchedules.length) {
        const weekdays: number[] = [];
        for (
          let date = new Date(routeStartJsDate);
          date <= routeEndJsDate;
          date.setDate(date.getDate() + 1)
        ) {
          let d = getDay(date);
          if (!weekdays.includes(d)) {
            weekdays.push(d);
          }
        }

        for (const schedule of recurrentSchedules) {
          const time = schedule.recurrentTimeInterval;
          if (!time) {
            if (schedule.weekdays!.some((w) => weekdays.includes(w))) {
              conflictingSchedules.push(schedule);
            }
            continue;
          }

          const timeParts = time.split(" - ");
          const startTime = timeParts[0];
          const endTime = timeParts[1] || timeParts[0];

          for (const weekday of schedule.weekdays!) {
            for (
              let date = new Date(routeStartJsDate);
              date <= routeEndJsDate;
              date.setDate(date.getDate() + 1)
            ) {
              if (getDay(date) === weekday) {
                const startDateTime = DateTime.fromISO(
                  `${date.toISOString().split("T")[0]}T${startTime}`
                );
                const endDateTime = DateTime.fromISO(
                  `${date.toISOString().split("T")[0]}T${endTime}`
                );
                const scheduleInterval = Interval.fromDateTimes(
                  startDateTime,
                  endDateTime
                );

                if (routeInterval.overlaps(scheduleInterval)) {
                  conflictingSchedules.push(schedule);
                  break;
                }

                const scheduleIntervalDays = scheduleInterval
                  .splitBy({ days: 1 })
                  .map((i) => i.start.toISODate());
                if (
                  routeIntervalDays.some((d) =>
                    scheduleIntervalDays.includes(d)
                  )
                ) {
                  schedulesOnRouteDays.push(schedule);
                  break;
                }
              }
            }
          }
        }
      }

      // INTERVAL
      for (const intervalSchedule of intervalSchedules) {
        const interval = Interval.fromDateTimes(
          DateTime.fromISO(intervalSchedule.startDate!),
          DateTime.fromISO(intervalSchedule.endDate!)
        );
        // console.log(
        //   "route start",
        //   routeInterval.start.toISOTime(),
        //   "route end",
        //   routeInterval.end.toISOTime(),
        //   "interval start",
        //   interval.start.toISOTime(),
        //   "interval end",
        //   interval.end.toISOTime()
        // );
        if (routeInterval.overlaps(interval)) {
          conflictingSchedules.push(intervalSchedule);
        }
        const intervalDays = interval
          .splitBy({ days: 1 })
          .map((i) => i.start.toISODate());
        if (routeIntervalDays.some((d) => intervalDays.includes(d))) {
          schedulesOnRouteDays.push(intervalSchedule);
        }
      }

      return { conflictingSchedules, schedulesOnRouteDays };
    }
  );

// True when assigning this driver to this route would conflict with a route they're
// already busy with (their reserved time overlaps the route's start-end). Only
// meaningful for a driver not already assigned to the route - if they are, that
// route's own interval is naturally excluded from the comparison below.
export const selectDriverRoutesConflict = (driverId: string, routeId: string) =>
  createSelector(
    (state: RootState) => state.data.driverTimeReserved.byDriverId[driverId],
    selectRouteFromToDateTimes(routeId),
    (driverTimeReserved, fromToDateTimes) => {
      if (!driverTimeReserved || !fromToDateTimes.from || !fromToDateTimes.to) {
        return false;
      }
      const routeInterval = Interval.fromDateTimes(
        fromToDateTimes.from,
        fromToDateTimes.to
      );
      return Object.entries(driverTimeReserved.data).some(
        ([otherRouteId, [from, to]]) =>
          otherRouteId !== routeId &&
          routeInterval.overlaps(
            // from/to are inclusive calendar dates, so the exclusive end needs to
            // be pushed a day past `to` - otherwise a route reserved on the exact
            // day this route starts or ends wouldn't register as a conflict.
            Interval.fromDateTimes(
              DateTime.fromISO(from),
              DateTime.fromISO(to).plus({ days: 1 })
            )
          )
      );
    }
  );
